import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { VehicleCard } from "@/components/VehicleCard";
import { TextToRentCTA, TextToRentBanner } from "@/components/TextToRentCTA";
import { Button } from "@/components/ui/button";
import {
  Car,
  DollarSign,
  MapPin,
  Shield,
  Truck,
  Briefcase,
  ArrowRight,
  Phone,
  CheckCircle,
  Clock,
  CreditCard,
  Gauge,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HomePage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        // Try to seed vehicles first
        await axios.post(`${API}/seed-vehicles`);
        // Then fetch them
        const response = await axios.get(`${API}/vehicles`);
        setVehicles(response.data);
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Header />

      {/* Hero Section */}
      <section className="hero-gradient relative overflow-hidden" data-testid="hero-section">
        <div className="container-custom px-6 md:px-12 lg:px-24 py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 mb-6">
                <DollarSign className="w-4 h-4 text-amber-400" />
                <span className="text-white/90 text-sm font-medium uppercase tracking-wide">
                  No Deposit Required
                </span>
              </div>
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
                Affordable Weekly
                <span className="block text-amber-400">Car Rentals</span>
              </h1>
              <p className="text-slate-300 text-lg md:text-xl mb-8 max-w-lg">
                We privately rent vehicles to those who seek to deliver food thru platforms such as 
                Grub Hub, Uber Eats, and Instacart or to those who simply need transportation to get 
                to and from work everyday. Just{" "}
                <span className="font-mono font-bold text-amber-400">$200/week</span>.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="sms:8592703755?body=car%20rental" data-testid="hero-cta">
                  <Button
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-white uppercase tracking-wide font-bold text-base px-8 py-6 btn-brutalist w-full sm:w-auto"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Text to Book
                  </Button>
                </a>
                <Link to="/fleet">
                  <Button
                    size="lg"
                    variant="outline"
                    className="bg-transparent border-2 border-white text-white hover:bg-white/10 uppercase tracking-wide font-bold text-base px-8 py-6 w-full sm:w-auto"
                    data-testid="hero-fleet-btn"
                  >
                    View Our Fleet
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative hidden lg:block">
              <div className="relative z-10">
                <img
                  src="https://images.unsplash.com/photo-1689866499892-53e5e619c3fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHBlcnNvbiUyMGRyaXZpbmclMjBjYXIlMjBzdW5zZXR8ZW58MHx8fHwxNzczNjE0MTk3fDA&ixlib=rb-4.1.0&q=85&w=800"
                  alt="Happy driver"
                  className="w-full max-w-md ml-auto shadow-2xl"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-amber-400 p-6 z-20">
                <span className="font-mono font-bold text-3xl text-slate-900 block">$200</span>
                <span className="text-slate-700 text-sm uppercase tracking-wide">Per Week</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-slate-50 noise-overlay" data-testid="features-section">
        <div className="container-custom relative z-10">
          <div className="text-left mb-12">
            <span className="text-primary text-sm font-bold uppercase tracking-widest">
              Benefits & Amenities
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mt-2">
              Why Choose Us
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: DollarSign,
                title: "$200/Week",
                description: "Affordable weekly rate. No deposit required to get started.",
              },
              {
                icon: CreditCard,
                title: "No Credit Checks",
                description: "We don't run credit checks. Everyone deserves reliable transportation.",
              },
              {
                icon: Gauge,
                title: "Unlimited Mileage",
                description: "Unlimited in-town mileage allowed with no overage rates!",
              },
              {
                icon: Truck,
                title: "Delivery Ready",
                description: "Perfect for Grub Hub, Uber Eats, Instacart, and other delivery platforms.",
              },
            ].map((feature, i) => (
              <div key={i} className="policy-card bg-white">
                <feature.icon className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Preview */}
      <section className="section-padding" data-testid="fleet-preview">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div className="text-left">
              <span className="text-primary text-sm font-bold uppercase tracking-widest">
                Our Fleet
              </span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mt-2">
                Available Vehicles
              </h2>
            </div>
            <Link to="/fleet" className="mt-4 md:mt-0">
              <Button variant="link" className="text-primary font-medium p-0" data-testid="view-all-fleet">
                View All Vehicles
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-slate-100 aspect-[4/3] animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {vehicles.slice(0, 3).map((vehicle, index) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Requirements Section */}
      <section className="section-padding bg-slate-900 text-white" data-testid="policies-section">
        <div className="container-custom">
          <div className="text-left mb-12">
            <span className="text-amber-400 text-sm font-bold uppercase tracking-widest">
              Requirements
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mt-2">
              What You Need to Know
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Age Requirement */}
            <div className="bg-slate-800 p-8 border border-slate-700">
              <Shield className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-heading font-bold text-xl text-white mb-3">
                Age Requirement
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                To rent from us, you must be:
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-slate-300 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  25 years of age or older
                </li>
              </ul>
            </div>

            {/* License Requirement */}
            <div className="bg-slate-800 p-8 border border-slate-700">
              <Car className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-heading font-bold text-xl text-white mb-3">
                Valid License
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                You must have:
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-slate-300 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  A valid driver's license
                </li>
              </ul>
            </div>

            {/* Service Area */}
            <div className="bg-slate-800 p-8 border border-slate-700">
              <MapPin className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-heading font-bold text-xl text-white mb-3">
                Service Area
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Vehicles must remain within:
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-slate-300 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Lexington Metropolitan Area
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Text to Rent CTA Banner */}
      <TextToRentBanner />

      <Footer />
      <TextToRentCTA />
    </div>
  );
};

export default HomePage;
